import { useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@resource-kit/ui/react';
import { usePrintJob, type ListPrintConfig } from '@resource-kit/controllers/react';
import { usePermissions } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import { queryKeys } from '@lib/queryKeys';
import { STAGE_LABEL } from '../board/BatchBoard';
import { formatBatchDay } from '../board/formatBatchDay';
import PackagingCard, { PACKAGING_INK, PACKAGING_PAPER } from '../packaging/PackagingCard';
import { CARDS_PER_SHEET } from '../packaging/PackagingSheet';
import { fetchPackagingByDate } from '../packaging/packagingData';
import { PACKAGING_PRINT_PAGE, buildPackagingSheet } from '../packaging/packagingPrint';
import { deriveDepartureRows } from './departureRows';

interface DeparturePaneProps {
  date: string;
}

type PrintRow = { id: string };

/**
 * The "awaiting departure" pane (#2480): one row per driver whose jobs are
 * dispatched and not yet started, each with a Print button, plus one button
 * for everything. Printing goes through the same `buildPackagingSheet` as the
 * delivery-job list; the window must open in the click's own call stack, so
 * the click writes the ids to a ref and calls `start` with no `await` between.
 */
export function DeparturePane({ date }: DeparturePaneProps) {
  const { t, i18n } = useTranslation();
  const perms = usePermissions();
  const canRead = perms.includes(PERMISSIONS.PACKAGING_READ);
  const canPrint = perms.includes(PERMISSIONS.PACKAGING_EXPORT);
  const day = formatBatchDay(date, i18n.language);

  const query = useQuery({
    queryKey: queryKeys.dispatch.departure(date),
    queryFn: () => fetchPackagingByDate(date),
    enabled: canRead,
  });
  const rows = useMemo(
    () => (query.data && !query.isError ? deriveDepartureRows(query.data) : []),
    [query.data, query.isError],
  );
  const total = rows.reduce((sum, row) => sum + row.entries.length, 0);

  const pendingIds = useRef<readonly string[]>([]);
  const printConfig = useMemo<ListPrintConfig<PrintRow>>(
    () => ({
      permission: PERMISSIONS.PACKAGING_EXPORT,
      page: PACKAGING_PRINT_PAGE,
      buildSheet: ({ rows: picked }) =>
        buildPackagingSheet(
          picked.map((row) => row.id),
          true,
        ),
    }),
    [],
  );
  const printJob = usePrintJob<PrintRow>({
    config: printConfig,
    fetchAllRows: () => Promise.resolve(pendingIds.current.map((id) => ({ id }))),
    selectedRows: [],
    renderDefault: () => null,
    documentTitle: t('dispatch.pane.departure.printTitle', { day }),
  });
  const print = (ids: readonly string[]) => {
    pendingIds.current = ids;
    printJob.start('all');
  };
  const busy = printJob.phase === 'loading';
  const previewEntries = rows[0]?.entries.slice(0, 2) ?? [];

  const list = () => {
    if (!canRead)
      return <p className="px-4 py-3 text-sm">{t('dispatch.pane.departure.noAccess')}</p>;
    if (query.isError) {
      return (
        <div className="flex items-center gap-3 px-4 py-3 text-sm">
          <span>{t('dispatch.pane.departure.loadFailed')}</span>
          <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
            {t('ui.retry')}
          </Button>
        </div>
      );
    }
    if (query.isLoading) {
      return (
        <div className="h-24 animate-pulse bg-muted/40" data-testid="dispatch-departure-loading" />
      );
    }
    if (rows.length === 0) {
      return (
        <p className="px-4 py-3 text-sm text-muted-foreground">
          {t('dispatch.pane.departure.empty')}
        </p>
      );
    }
    return rows.map((row, index) => (
      <div
        key={row.key}
        data-testid={`dispatch-departure-row-${row.key}`}
        className={`grid grid-cols-[10px_minmax(0,1fr)_64px_80px_auto] items-center gap-3 px-4 py-2.5 text-[13px] ${
          index > 0 ? 'border-t border-border/60' : ''
        }`}
      >
        <span
          className="h-2.5 w-2.5 rounded-micro"
          style={{ backgroundColor: row.color }}
          aria-hidden="true"
        />
        <span className="truncate font-medium">{row.name}</span>
        <span className="text-right font-mono">
          {t('dispatch.pane.countBadge', { count: row.entries.length })}
        </span>
        <span className="text-[12.5px] text-muted-foreground">
          {t('dispatch.pane.departure.pages', { count: row.pages })}
        </span>
        {canPrint ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            data-testid={`dispatch-departure-print-${row.key}`}
            onClick={() => print(row.jobIds)}
          >
            {t('dispatch.pane.departure.print')}
          </Button>
        ) : (
          <span />
        )}
      </div>
    ));
  };

  return (
    <div
      data-testid="dispatch-pane-departure"
      className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]"
    >
      <section className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h3 className="text-[14.5px] font-semibold">{t('dispatch.pane.departure.title')}</h3>
          <p className="text-[12.5px] text-muted-foreground">
            {t('dispatch.pane.departure.subtitle', { perSheet: CARDS_PER_SHEET })}
          </p>
        </div>
        <div className="overflow-hidden rounded-xl border border-border bg-card">{list()}</div>
        {previewEntries.length > 0 ? (
          <div data-testid="dispatch-departure-preview" className="flex flex-wrap gap-3">
            {previewEntries.map((entry) => (
              <div
                key={entry.job_id}
                className="overflow-hidden rounded-sm shadow-sm"
                style={{ backgroundColor: PACKAGING_PAPER, color: PACKAGING_INK }}
              >
                <PackagingCard entry={entry} />
              </div>
            ))}
          </div>
        ) : null}
      </section>
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
        <p className="text-[12.5px] text-muted-foreground">
          {t('dispatch.pane.departure.assignedOn', { day, count: total })}
        </p>
        {canPrint ? (
          <Button
            className="h-11 w-full text-[14.5px]"
            disabled={busy || total === 0}
            data-testid="dispatch-departure-print-all"
            onClick={() => print(rows.flatMap((row) => row.jobIds))}
          >
            {t('dispatch.pane.departure.printAll', { count: total })}
          </Button>
        ) : null}
        <p className="text-[12.5px] text-muted-foreground">
          {t('dispatch.pane.departure.note', { enRoute: t(STAGE_LABEL.en_route) })}
        </p>
        {printJob.error ? <p className="text-[12.5px] text-destructive">{printJob.error}</p> : null}
      </div>
      {printJob.sheet !== null && printJob.mount
        ? createPortal(printJob.sheet, printJob.mount)
        : null}
    </div>
  );
}
